/**
 * Centralized calculation helper for Sales Invoices (Frontend)
 */

/**
 * Helper to safely parse floats with default 0
 */
export const safeParseFloat = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'object' && val.$numberDecimal) {
    return parseFloat(val.$numberDecimal) || 0;
  }
  const parsed = parseFloat(val);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format float to fixed 2 decimal string
 */
export const formatDecimal = (val) => {
  return safeParseFloat(val).toFixed(2);
};

/**
 * Calculate row item amounts, discounts, taxable value, GST, and total.
 * @param {Object} row - Sales invoice line item row
 * @returns {Object} Updated row object with calculated values
 */
export const calculateRowItem = (row) => {
  const qty = safeParseFloat(row.qty || row.Quantity);
  const rate = safeParseFloat(row.rate || row.Rate);
  const discount = safeParseFloat(row.discount || row.DiscountPercentage);
  const discountQty = safeParseFloat(row.discountQty || row.DiscountAmountPerUnit);
  const discountAmountGiven = safeParseFloat(row.discountAmount || row.DiscountAmount);
  const gstPercentage = safeParseFloat(row.gstPercentage || row.GstPercentage || row.GSTRate);
  const taxType = row.taxType || (safeParseFloat(row.IGSTPercentage || row.igstRate) > 0 ? "IGST" : "SGST_CGST");
  const cessAmount = safeParseFloat(row.cessAmount || row.Cess);
  const otherCharges = safeParseFloat(row.otherCharges || row.OtherCharges);
  
  const amount = qty * rate;
  let discountAmount = 0;

  if (discount > 0) {
    discountAmount = (amount * discount) / 100;
  } else if (discountQty > 0) {
    discountAmount = qty * discountQty;
  } else if (discountAmountGiven > 0) {
    discountAmount = discountAmountGiven;
  }

  // Safety protection: Discount amount can NEVER exceed gross amount!
  if (discountAmount > amount) {
    discountAmount = amount;
  }

  const taxableValue = Math.max(0, amount - discountAmount) + otherCharges;

  let cgstRate = 0, sgstRate = 0, igstRate = 0;
  let cgstAmount = 0, sgstAmount = 0, igstAmount = 0;

  if (gstPercentage > 0) {
    if (taxType === "SGST_CGST") {
      cgstRate = sgstRate = gstPercentage / 2;
      cgstAmount = (taxableValue * cgstRate) / 100;
      sgstAmount = (taxableValue * sgstRate) / 100;
    } else if (taxType === "IGST") {
      igstRate = gstPercentage;
      igstAmount = (taxableValue * igstRate) / 100;
    }
  }

  const total = parseFloat((taxableValue + cgstAmount + sgstAmount + igstAmount + cessAmount).toFixed(2));

  return {
    ...row,
    qty,
    rate,
    amount: amount.toFixed(2),
    discountAmount: discountAmount.toFixed(2),
    taxableValue: taxableValue.toFixed(2),
    cgstRate,
    sgstRate,
    igstRate,
    cgstAmount: cgstAmount.toFixed(2),
    sgstAmount: sgstAmount.toFixed(2),
    igstAmount: igstAmount.toFixed(2),
    total: total.toFixed(2)
  };
};

/**
 * Calculate totals across all rows in a Sales Invoice.
 * @param {Array} rows - List of line items
 * @param {Number} globalOtherCharges - Additional invoice-level other charges
 * @param {Number} globalCessAmount - Additional invoice-level cess
 * @returns {Object} Invoice summary totals
 */
export const calculateInvoiceSummary = (rows = [], globalOtherCharges = 0, globalCessAmount = 0) => {
  let totalQty = 0;
  let totalGrossAmount = 0;
  let totalDiscountAmount = 0;
  let totalOtherCharges = 0;
  let totalTaxableValue = 0;
  let totalIgstAmount = 0;
  let totalSgstAmount = 0;
  let totalCgstAmount = 0;
  let totalCessAmount = 0;
  let grandTotal = 0;

  rows.forEach((row) => {
    const calcRow = calculateRowItem(row);
    const qty = safeParseFloat(calcRow.qty);
    const rate = safeParseFloat(calcRow.rate);

    totalQty += qty;
    totalGrossAmount += qty * rate;
    totalDiscountAmount += safeParseFloat(calcRow.discountAmount);
    totalOtherCharges += safeParseFloat(calcRow.otherCharges || row.OtherCharges);
    totalTaxableValue += safeParseFloat(calcRow.taxableValue);
    totalIgstAmount += safeParseFloat(calcRow.igstAmount);
    totalSgstAmount += safeParseFloat(calcRow.sgstAmount);
    totalCgstAmount += safeParseFloat(calcRow.cgstAmount);
    totalCessAmount += safeParseFloat(calcRow.cessAmount || row.Cess);
    grandTotal += safeParseFloat(calcRow.total);
  });

  const finalGrandTotal = grandTotal + safeParseFloat(globalOtherCharges) + safeParseFloat(globalCessAmount);

  return {
    totalQty: totalQty.toFixed(2),
    totalGrossAmount: totalGrossAmount.toFixed(2),
    totalDiscountAmount: totalDiscountAmount.toFixed(2),
    totalOtherCharges: totalOtherCharges.toFixed(2),
    totalTaxableValue: totalTaxableValue.toFixed(2),
    totalIgstAmount: totalIgstAmount.toFixed(2),
    totalSgstAmount: totalSgstAmount.toFixed(2),
    totalCgstAmount: totalCgstAmount.toFixed(2),
    totalCessAmount: totalCessAmount.toFixed(2),
    grandTotal: grandTotal.toFixed(2),
    finalGrandTotal: finalGrandTotal.toFixed(2),
    roundedGrandTotal: Math.round(finalGrandTotal)
  };
};
